import { useRef, useEffect, useCallback } from 'react';
import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { useAppDispatch, useAppSelector } from 'store/hook';
import TireLeakageAPI, {
  type TireLeakageAnalysisResponse,
  type TransformedTireLeakageResponse,
  type TireData,
} from 'api/tire-leakage.api';
import dayjs from 'dayjs';

interface TireLeakageState {
  data: TransformedTireLeakageResponse | null;
  loading: boolean;
  error: string | null;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

// Helper function to transform flat API response to grouped structure
const transformTireLeakageData = (
  apiResponse: TireLeakageAnalysisResponse
): TransformedTireLeakageResponse => {
  const groupedData: Record<
    string,
    Record<string, Record<string, TireData & { leakageType: 'fast' | 'slow' }>>
  > = {};

  const uniqueAssets = new Set<string>();

  const processLeakages = (tires: TireData[], leakageType: 'fast' | 'slow') => {
    tires.forEach((tire) => {
      const timeKey = dayjs(tire.start_ts).format('YYYY-MM-DD HH:00:00');
      const assetId = tire.device;

      uniqueAssets.add(assetId);

      if (!groupedData[timeKey]) {
        groupedData[timeKey] = {};
      }

      if (!groupedData[timeKey][assetId]) {
        groupedData[timeKey][assetId] = {};
      }

      const tireKey = `${leakageType}_${tire?.side_of_truck || 'Unknown'}_${
        tire.start_ts
      }`;

      groupedData[timeKey][assetId][tireKey] = {
        ...tire,
        assetId,
        leakageType,
      };
    });
  };

  processLeakages(apiResponse.data.fast, 'fast');
  processLeakages(apiResponse.data.slow, 'slow');

  // Convert to array format and add totalLeakages per asset
  const dataArray = Object.entries(groupedData).map(([time, assets]) => {
    const formattedAssets: Record<string, any> = {};

    Object.entries(assets).forEach(([assetId, tires]) => {
      formattedAssets[assetId] = {
        ...tires,
        totalLeakages: Object.keys(tires).length,
      };
    });

    return {
      time,
      assets: formattedAssets,
    };
  });

  // Sort by time
  dataArray.sort((a, b) => dayjs(a.time).unix() - dayjs(b.time).unix());

  return {
    success: apiResponse.success,
    data: dataArray,
    totalLeakages:
      (apiResponse.totalRecords?.fast ?? apiResponse.data.fast.length) +
      (apiResponse.totalRecords?.slow ?? apiResponse.data.slow.length),
    totalAssets: uniqueAssets.size,
    analysisPeriod: apiResponse.analysisPeriod,
  };
};

const initialState: TireLeakageState = {
  data: null,
  loading: false,
  error: null,
  dateRange: {
    startDate: '',
    endDate: '',
  },
};

// Async thunk for fetching tire leakage analysis
export const fetchTireLeakageAnalysis = createAsyncThunk(
  'tireLeakage/fetchAnalysis',
  async (
    {
      customerId,
      startDate,
      endDate,
      api,
    }: {
      customerId: string;
      startDate: string;
      endDate: string;
      api: TireLeakageAPI;
    },
    { getState }
  ) => {
    // API is now passed from the hook context
    const response = await api.getAnalysis(customerId, startDate, endDate);

    // Transform the flat API response to grouped structure
    return transformTireLeakageData(response);
  }
);

const tireLeakageSlice = createSlice({
  name: 'tireLeakage',
  initialState,
  reducers: {
    setDateRange: (
      state,
      action: PayloadAction<{ startDate: string; endDate: string }>
    ) => {
      state.dateRange = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTireLeakageAnalysis.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTireLeakageAnalysis.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchTireLeakageAnalysis.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message ?? 'Failed to fetch tire leakage analysis';
      });
  },
});

export const { setDateRange, clearError, resetState } =
  tireLeakageSlice.actions;
export default tireLeakageSlice.reducer;

/**
 * Custom hook to access tire leakage service
 */
export const useTireLeakageService = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.tireLeakageReducer);

  // Instantiate API here inside the hook context
  // This is required because BaseAPI uses useAppSelector in its constructor
  const api = new TireLeakageAPI();
  const apiRef = useRef(api);

  apiRef.current = api;

  const fetchAnalysis = useCallback(
    async (customerId: string, startDate: string, endDate: string) => {
      return await dispatch(
        fetchTireLeakageAnalysis({
          customerId,
          startDate,
          endDate,
          api: apiRef.current,
        })
      );
    },
    [dispatch]
  );

  const getGeopoints = async (
    deviceId: string,
    startTimestamp: string,
    endTimestamp: string
  ) => {
    return await api.getGeopoints(deviceId, startTimestamp, endTimestamp);
  };

  return {
    ...state,
    fetchTireLeakageAnalysis: fetchAnalysis,
    getGeopoints,
    setDateRange: (startDate: string, endDate: string) =>
      dispatch(setDateRange({ startDate, endDate })),
    clearError: () => dispatch(clearError()),
    resetState: () => dispatch(resetState()),
  };
};
